import { Alert, Box, Button, CircularProgress, Paper, Skeleton, Typography } from '@mui/material';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MatchingProductItem, PaginatedMatchingProducts } from '../types';
import { getMatchingOffers } from '../services/alertsService';
import { MatchingProductCard } from './MatchingProductCard';

const PAGE_LIMIT = 10;

const EMPTY_META = { total: 0, page: 1, limit: PAGE_LIMIT, totalPages: 0 };

interface MatchingOffersSectionProps {
  readonly hasActiveAlerts: boolean;
}

export function MatchingOffersSection({ hasActiveAlerts }: MatchingOffersSectionProps) {
  const { t } = useTranslation();

  const [items, setItems] = useState<MatchingProductItem[]>([]);
  const [meta, setMeta] = useState<PaginatedMatchingProducts['meta']>(EMPTY_META);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);

  const loadItems = useCallback(async (pageNum: number, append: boolean) => {
    try {
      if (pageNum === 1 && !append) {
        setLoading(true);
        setError(false);
      } else {
        setLoadingMore(true);
      }
      const result = await getMatchingOffers(pageNum, PAGE_LIMIT);
      setItems(prev => (append ? [...prev, ...result.data] : result.data));
      setMeta(result.meta);
      setPage(pageNum);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    if (hasActiveAlerts) {
      loadItems(1, false);
    }
  }, [hasActiveAlerts, loadItems]);

  if (!hasActiveAlerts) return null;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        p: 2,
      }}
      data-testid="matching-offers-section"
    >
      {/* Section header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '16px',
            bgcolor: 'secondary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <NotificationsNoneOutlinedIcon sx={{ color: 'text.primary', fontSize: 24 }} />
        </Box>
        <Typography variant="h6" fontWeight={700}>
          {t('alerts.matching.title', 'Catégories correspondant aux alertes')}
        </Typography>
      </Box>

      {/* Loading state */}
      {loading && (
        <Box data-testid="matching-offers-loading">
          {[1, 2, 3].map(i => (
            <Box key={i} sx={{ mb: 1.5 }}>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: '12px' }} />
            </Box>
          ))}
        </Box>
      )}

      {/* Error state */}
      {!loading && error && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => loadItems(1, false)}
              data-testid="matching-offers-retry-button"
            >
              {t('common.retry', 'Réessayer')}
            </Button>
          }
          data-testid="matching-offers-error"
        >
          {t(
            'alerts.matching.error',
            'Impossible de charger les catégories correspondantes. Veuillez réessayer.'
          )}
        </Alert>
      )}

      {/* Empty state */}
      {!loading && !error && items.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }} data-testid="matching-offers-empty">
          <Typography variant="body2" color="text.secondary">
            {t('alerts.matching.empty', 'Aucune catégorie ne correspond à vos alertes actuelles.')}
          </Typography>
        </Box>
      )}

      {/* Products list */}
      {!loading && !error && items.length > 0 && (
        <Box data-testid="matching-offers-list">
          {items.map(product => (
            <MatchingProductCard key={product.productId} product={product} />
          ))}

          {/* Load more */}
          {items.length < meta.total && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
              <Button
                variant="outlined"
                onClick={() => loadItems(page + 1, true)}
                disabled={loadingMore}
                data-testid="matching-offers-load-more"
                sx={{ textTransform: 'none', borderRadius: 3, px: 4 }}
              >
                {loadingMore ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                {loadingMore
                  ? t('alerts.matching.loadingMore', 'Chargement...')
                  : t('alerts.matching.loadMore', 'Voir plus')}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
}
